import React, { useEffect } from 'react';
import { Home, Droplets, Car, Layout, Grid, Building2, Shield, Leaf } from 'lucide-react';
import { ServiceCard } from '../components/ServiceCard';
import { CTABanner } from '../components/CTABanner';
export function ServicesHub() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  const services = [{
    title: 'Residential Soft Washing',
    description: "Safe, low-pressure cleaning for your home's exterior siding, stucco, and brick.",
    icon: Home,
    linkTo: '/services/residential'
  }, {
    title: 'Roof Cleaning',
    description: 'Remove black streaks, moss, and lichen to extend the life of your shingles.',
    icon: Shield,
    linkTo: '/services/roof'
  }, {
    title: 'Gutter Cleaning',
    description: 'Clear debris and brighten exteriors to remove unsightly tiger stripes.',
    icon: Droplets,
    linkTo: '/services/gutter'
  }, {
    title: 'Driveway & Concrete',
    description: 'Remove oil stains, rust, and grime from driveways, walkways, and patios.',
    icon: Car,
    linkTo: '/services/driveway'
  }, {
    title: 'Deck & Patio Cleaning',
    description: 'Restore wood and composite decks safely without splintering or damage.',
    icon: Layout,
    linkTo: '/services/deck'
  }, {
    title: 'Fence Cleaning',
    description: 'Revitalize wood, vinyl, and aluminum fences, removing green algae and graying.',
    icon: Grid,
    linkTo: '/services/fence'
  }, {
    title: 'Commercial Soft Washing',
    description: 'Keep your business exterior pristine to attract customers and maintain value.',
    icon: Building2,
    linkTo: '/commercial'
  }];
  return <div className="w-full" data-id="element-503">
      {/* Hero */}
      <section className="relative pt-32 pb-20 md:pt-40 md:pb-28 overflow-hidden bg-navy" data-id="element-504">
        <div className="absolute inset-0 bg-[url('https://images.unsplash.com/photo-1558611848-73f7eb4001a1?w=1200')] bg-cover bg-center opacity-20" data-id="element-505"></div>
        <div className="relative z-20 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center" data-id="element-506">
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-montserrat font-black text-white mb-6" data-id="element-507">
            Our Services
          </h1>
          <p className="text-xl text-cream max-w-3xl mx-auto" data-id="element-508">
            Comprehensive exterior cleaning solutions for every surface of your
            property.
          </p>
        </div>
      </section>

      {/* Services Grid */}
      <section className="py-16 md:py-24 bg-gray-50" data-id="element-509">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-510">
          <div className="text-center mb-16" data-id="element-511">
            <h2 className="text-3xl font-montserrat font-bold text-navy mb-4" data-id="element-512">
              Professional Cleaning for Every Surface
            </h2>
            <p className="text-gray-600 max-w-2xl mx-auto" data-id="element-513">
              We use specialized soft washing techniques tailored to each
              specific material, ensuring a deep clean without the risk of
              damage associated with high-pressure washing.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8" data-id="element-514">
            {services.map((service, index) => <ServiceCard key={index} title={service.title} description={service.description} icon={service.icon as any} linkTo={service.linkTo} data-id="element-515" />)}
          </div>
        </div>
      </section>

      {/* Residential vs Commercial */}
      <section className="py-16 bg-white" data-id="element-516">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-517">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12" data-id="element-518">
            <div className="bg-cream/30 p-10 rounded-2xl border border-cream" data-id="element-519">
              <Home className="w-12 h-12 text-navy mb-6" data-id="element-520" />
              <h3 className="text-2xl font-montserrat font-bold text-navy mb-4" data-id="element-521">
                Residential Services
              </h3>
              <p className="text-gray-700 mb-6" data-id="element-522">
                Your home is your biggest investment. Our residential soft
                washing services protect that investment by safely removing
                harmful organic growth that can degrade your exterior surfaces
                over time. We boost your curb appeal instantly.
              </p>
              <ul className="space-y-2 text-gray-600" data-id="element-523">
                <li className="flex items-center" data-id="element-524">
                  <Leaf className="w-5 h-5 text-light-blue mr-2" data-id="element-525" /> Safe for
                  family and pets
                </li>
                <li className="flex items-center" data-id="element-526">
                  <Leaf className="w-5 h-5 text-light-blue mr-2" data-id="element-527" /> Plant and
                  landscaping protection
                </li>
                <li className="flex items-center" data-id="element-528">
                  <Leaf className="w-5 h-5 text-light-blue mr-2" data-id="element-529" /> Long-lasting
                  results
                </li>
              </ul>
            </div>
            <div className="bg-navy p-10 rounded-2xl text-white shadow-xl relative overflow-hidden" data-id="element-530">
              <div className="absolute top-0 right-0 -mr-10 -mt-10 w-40 h-40 bg-light-blue opacity-20 rounded-full blur-2xl" data-id="element-531"></div>
              <Building2 className="w-12 h-12 text-light-blue mb-6 relative z-10" data-id="element-532" />
              <h3 className="text-2xl font-montserrat font-bold mb-4 relative z-10" data-id="element-533">
                Commercial Services
              </h3>
              <p className="text-cream mb-6 relative z-10" data-id="element-534">
                First impressions matter. We help businesses, HOAs, and property
                managers maintain a pristine, professional appearance. We offer
                flexible scheduling to minimize disruption to your operations.
              </p>
              <ul className="space-y-2 text-cream relative z-10" data-id="element-535">
                <li className="flex items-center" data-id="element-536">
                  <Shield className="w-5 h-5 text-light-blue mr-2" data-id="element-537" /> Fully
                  insured for commercial work
                </li>
                <li className="flex items-center" data-id="element-538">
                  <Shield className="w-5 h-5 text-light-blue mr-2" data-id="element-539" />{' '}
                  After-hours scheduling available
                </li>
                <li className="flex items-center" data-id="element-540">
                  <Shield className="w-5 h-5 text-light-blue mr-2" data-id="element-541" /> Routine
                  maintenance plans
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      <CTABanner data-id="element-542" />
    </div>;
}